// One estimate line per catalog item.
//
// A placeable room element (HEPA, light, door …) reaches the Estimate through
// two paths that are each correct on their own:
//   - every symbol placed / generated on the canvas, priced by
//     sp_GetEstimateDesignRollup (grouped by item, across ALL drawings), and
//   - the not-yet-placed remainder (required − placed per room), priced by
//     Estimate.jsx's roomElementsRollup.
// Concatenated, one item showed up as two lines. This folds the two back into
// a single line whose quantity is placed + remaining, keeping a breakdown so
// the estimator can still see where each part came from.
//
// Pure and exact: quantity, direct cost and sell value are all linear in
// quantity, so summing the two rows' values equals pricing the summed quantity.
// Only the canvas-drawn rows (SourceDetail 'drawn' — tagged in Estimate.jsx
// on the sp_GetEstimateDesignRollup rows) and room-element rows
// (SourceDetail 'element') are merged — equipment, utility, distribution and
// manual rows are left as their own lines.

const isDrawn = (r) => r.SourceDetail === "drawn";
const isElement = (r) => r.SourceDetail === "element";
const keyOf = (r) => `${r.EntityKind}:${r.EntityID}:${r.AlternateID ?? ""}`;

// " — A101: 5 · A102: 13" (empty when there is no per-drawing data).
function sheetBreakdown(placedBySheet, row) {
  const parts = placedBySheet?.get(`${row.EntityKind}:${row.EntityID}`);
  if (!parts?.length) return ", across all drawings";
  return " — " + parts.map(p => `${p.label}: ${p.count}`).join(" · ");
}

const fmtQty = (n) => (+n).toLocaleString(undefined, { maximumFractionDigits: 2 });

// placedBySheet (optional): Map of "<item|assembly>:<id>" -> [{ label, count }] — how many of the
// item are placed on each drawing; shown in the merged line's breakdown.
export function mergeDrawnAndElementRows(rows, { placedBySheet } = {}) {
  const drawnByKey = new Map();
  const elementByKey = new Map();
  for (const r of rows) {
    if (isDrawn(r)) { const k = keyOf(r); (drawnByKey.get(k) || drawnByKey.set(k, []).get(k)).push(r); }
    else if (isElement(r)) { const k = keyOf(r); (elementByKey.get(k) || elementByKey.set(k, []).get(k)).push(r); }
  }

  const out = [];
  const consumed = new Set();
  for (const r of rows) {
    if (consumed.has(r)) continue;
    const k = keyOf(r);
    const drawn = drawnByKey.get(k);
    const elements = elementByKey.get(k);
    // Merge only when BOTH a drawn row and an element row exist for the item.
    if (!(drawn && elements && (isDrawn(r) || isElement(r)))) { out.push(r); continue; }
    // Emit the merged line at the position of the first of the group's rows.
    if (!consumed.has(drawn[0]) && !consumed.has(elements[0])) {
      const group = [...drawn, ...elements];
      group.forEach(g => consumed.add(g));
      const sum = (f) => group.reduce((t, g) => t + (+g[f] || 0), 0);
      const placedQty = drawn.reduce((t, g) => t + (+g.TotalQty || 0), 0);
      const placedInstances = drawn.reduce((t, g) => t + (+g.MatchedInstanceCount || 0), 0);
      const remaining = elements.reduce((t, g) => t + (+g.TotalQty || 0), 0);
      const base = drawn[0];
      out.push({
        ...base,
        TotalQty: sum("TotalQty"),
        DirectCost: sum("DirectCost"),
        SellValue: sum("SellValue"),
        MatchedInstanceCount: placedInstances,
        Contributions: [
          { elementId: `placed:${k}`, roomName: "Placed on the canvas", summary: `${fmtQty(placedQty)} priced from ${placedInstances} placed symbol${placedInstances === 1 ? "" : "s"}${sheetBreakdown(placedBySheet, base)}` },
          ...elements.flatMap(e => (e.Contributions || []).map(c => ({ ...c, summary: `${fmtQty(c.qty)} not yet placed — ${c.summary}` }))),
        ],
        MergedBreakdown: { placedQty, placedInstances, remainingQty: remaining },
      });
    }
  }
  return out;
}
